// Limit the number of identifiers sent due to large payload
import { Box, type BoxT } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import * as React from 'react';
import type { useQuizResultActions } from '@mentimeter/quiz';
import { useState } from 'react';
import { EyeOffIcon } from '@mentimeter/ragnar-visuals';
import { AnswerStatusIcon } from './AnswerStatusIcon';

const IDENTIFIER_UPDATE_LIMIT = 500;

const getBackgroundColor = (
  isCorrect: boolean,
  isHovered: boolean,
): BoxT['bg'] => {
  if (isCorrect) {
    return isHovered ? 'positiveWeak' : 'positiveWeakest';
  }
  return isHovered ? 'neutral' : 'neutralWeakest';
};

export const Card = ({
  index,
  answer,
  correct,
  blurred,
  identifiers,
  onMarkAnswer,
  isReadOnly,
}: {
  index: number;
  answer: string;
  correct: boolean;
  blurred: boolean;
  identifiers: string[];
  isReadOnly: boolean;
  onMarkAnswer:
    | ReturnType<typeof useQuizResultActions>['markAnswer']
    | undefined;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const canMarkAnswer =
    !isReadOnly &&
    onMarkAnswer !== undefined &&
    identifiers.length <= IDENTIFIER_UPDATE_LIMIT;

  return (
    <Box
      width="100%"
      alignItems="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        bg={getBackgroundColor(correct, isHovered)}
        width="100%"
        p="space10"
        borderRadius="3xl"
        flexDirection="row"
      >
        <Box width="100%" flex="1 1 auto">
          <Text
            color={
              blurred
                ? 'transparent'
                : correct
                  ? 'onPositiveWeak'
                  : 'onNeutralWeak'
            }
            fontSize="175"
            extend={({ theme }) => ({
              textShadow: blurred
                ? `${theme.colors.textWeak} 2px -2px 16px`
                : 'none',
            })}
          >
            {answer}
          </Text>
          {identifiers.length > 1 && (
            <Text
              className="control"
              mt="space3"
              color="textWeaker"
              fontSize="125"
              extend={() => ({
                opacity: blurred ? 0 : 1,
                transition: 'opacity 0.25s ease',
              })}
            >
              {identifiers.length} responses
            </Text>
          )}
        </Box>

        <Box position="absolute" top="14px" right="14px" flexDirection="row">
          {canMarkAnswer && (
            <Box
              extend={() => ({
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.25s ease',
              })}
            >
              <Clickable
                className="control"
                data-testid={`mark-${
                  blurred ? 'appropriate' : 'inappropriate'
                }-${index}`}
                aria-label={`Mark ${blurred ? 'appropriate' : 'inappropriate'}`}
                onClick={() =>
                  onMarkAnswer({
                    identifiers,
                    marked_correct: false,
                    marked_inappropriate: !blurred,
                  })
                }
                p="space2"
              >
                <EyeOffIcon size={5} />
              </Clickable>
            </Box>
          )}
          {canMarkAnswer ? (
            <Clickable
              data-testid={`mark-${correct ? 'incorrect' : 'correct'}-${index}`}
              aria-label={`Mark ${correct ? 'incorrect' : 'correct'}`}
              onClick={() =>
                onMarkAnswer({
                  identifiers,
                  marked_correct: !correct,
                  marked_inappropriate: blurred,
                })
              }
              p="space2"
              extend={() => ({
                opacity: blurred ? 0 : 1,
                pointerEvents: canMarkAnswer && !blurred ? undefined : 'none',
                '&:hover': {
                  opacity: blurred ? 0 : 1,
                },
              })}
            >
              <AnswerStatusIcon correct={correct} />
            </Clickable>
          ) : (
            <Box
              p="space2"
              extend={() => ({
                opacity: blurred ? 0 : 1,
              })}
            >
              <AnswerStatusIcon correct={correct} />
            </Box>
          )}
        </Box>
      </Box>

      {identifiers.length > 1 && (
        <Box
          mt={1}
          width="97%"
          bg={getBackgroundColor(correct, isHovered)}
          extend={({ theme }) => ({
            height: `${theme.radii[1]}px`,
            borderBottomLeftRadius: `${theme.radii[1]}px`,
            borderBottomRightRadius: `${theme.radii[1]}px`,
          })}
        />
      )}
    </Box>
  );
};
